import { useCallback, useEffect, useState } from "react";
import { Message, MessageData, MessageSender } from "./types";
import { FiBiVoiceBottomBarProps } from "./FiBiVoiceBottomBar";
import { useSnapshot } from "valtio";
import { userDataStore } from "../../../../../../store/userData";
import { toast } from "react-toastify";
import useWebSocket from "react-use-websocket";
import {
  formatTime,
  getBinaryChunk,
  getErrorMessage,
  getText,
  isBinaryChunkResponse,
  isEnd,
  getIsMediaSourceSupported,
  isTextResponse,
} from "./data";
import { isError } from "@tanstack/react-query";
import { BaseFiBiInterviewWSEndpointB2B } from "../../../../../../api/request";
import { useSpeechSend } from "./useSpeechSend";
import useFibiMediaSource from "./useFibiMediaSource";
import useFibiAudioContext from "./useFibiAudioContext";
import { businessFibiClientStore } from "../../../../../../store/businessFibiClient";

const useFiBiVoiceBottomBar = ({
  businessProps,
  onAddToChatHistory,
  audioRef,
  allMessages,
  onInterim,
  isDirectUser,
}: FiBiVoiceBottomBarProps) => {
  const { userData } = useSnapshot(userDataStore);
  const { storedCurrentSessionStartTime } = useSnapshot(
    businessFibiClientStore
  );

  const { startListening, stopListening, listening } = useSpeechSend(
    (msg) => sendMessage(msg),
    (interim) => onInterim(interim),
    () => stopPlayingAudio()
  );

  const isMediaSourceSupported = getIsMediaSourceSupported();

  // UI state
  // const [isListening, setIsListening] = useState(false);
  const [fibiProcessing, setFibiProcessing] = useState(false);
  const [fibiSpeaking, setFibiSpeaking] = useState(false);

  const handlePlaybackEnded = useCallback(() => {
    setFibiProcessing(false);
    setFibiSpeaking(false);
    startListening();
    // startListening can't add as dependencies, cause it will cause infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePlaybackPlaying = useCallback(() => {
    setFibiSpeaking(true);
  }, []);

  const {
    processQueuedChunks,
    pendingChunks,
    audioElement,
    endOfStreamReceived,
    initNewStream,
    sourceBufferRef,
  } = useFibiMediaSource({
    handlePlaybackEnded,
    handlePlaybackPlaying,
    enabled: isMediaSourceSupported,
  });

  const {
    isNetworkError,
    networkStatus,
    setNetworkStatus,
    setIsNetworkError,
    lastStatusUpdateRef,
    lastChunkReceivedAtRef,
    ensureAudioContext,
    processChunk,
    chunkArrivalTimesRef,
    networkErrorTimeoutRef,
    totalChunksReceivedRef,
    audioContextRef,
    resetAudioState,
  } = useFibiAudioContext({
    setFibiSpeaking,
    setFibiProcessing,
    startListening,
    enabled: !isMediaSourceSupported,
  });

  // WebSocket streaming
  const { sendJsonMessage } = useWebSocket(BaseFiBiInterviewWSEndpointB2B, {
    share: true,
    shouldReconnect: () => true,
    onMessage: async (event) => {
      const data = JSON.parse(event.data);
      const binaryChunk = getBinaryChunk(data);
      const isBinaryChunk = isBinaryChunkResponse(data);

      const now = Date.now();

      if (isTextResponse(data)) {
        const text = getText(data);
        if (text)
          onAddToChatHistory({ sender: MessageSender.bot, message: text });
      } else if (isBinaryChunk && binaryChunk) {
        if (isMediaSourceSupported) {
          if (!audioElement.current) return;
          const arrayBuffer = binaryChunk.data;
          const uint8Array = new Uint8Array(arrayBuffer);

          pendingChunks.current.push(uint8Array);

          processQueuedChunks();

          if (
            !audioElement.current.paused ||
            pendingChunks.current.length === 1
          ) {
            audioElement.current.play().catch(console.error);
          }
        } else {
          const bin = getBinaryChunk(data);
          if (!bin) return;
          // DIAGNOSTIC: Track arrival times
          if (lastChunkReceivedAtRef.current !== null) {
            const diff = now - lastChunkReceivedAtRef.current;
            const timeSinceLastStatus = now - lastStatusUpdateRef.current;
            chunkArrivalTimesRef.current.push(diff);
            // Clear any existing timeout
            if (networkErrorTimeoutRef.current) {
              clearTimeout(networkErrorTimeoutRef.current);
            }
            // Only update status if it's been at least 1000ms since last update
            if (timeSinceLastStatus >= 1000) {
              if (diff > 200) {
                console.warn(
                  `⚠️ Network jitter detected: ${diff}ms between chunks`
                );
                setNetworkStatus("unstable");
                setIsNetworkError(true);
              } else {
                setNetworkStatus("stable");
                setIsNetworkError(false);
              }
              lastStatusUpdateRef.current = now;
            }
          }
          lastChunkReceivedAtRef.current = now;
          totalChunksReceivedRef.current += 1;
          if (!audioContextRef.current) {
            await ensureAudioContext();
            setFibiSpeaking(true);
          }
          await processChunk(new Uint8Array(bin.data));
        }
      } else if (isEnd(data)) {
        if (isMediaSourceSupported) {
          endOfStreamReceived.current = true;
          processQueuedChunks();
        } else {
          await processChunk(new Uint8Array(), true);
        }
        // After all chunks received, report stats
        // const arrivals = chunkArrivalTimesRef.current;
        // if (arrivals.length > 1) {
        //   const avgInterval =
        //     arrivals.reduce((a, b) => a + b, 0) / arrivals.length;
        //   console.log(
        //     `📈 Chunk rate: ${(1000 / avgInterval).toFixed(2)} chunks/sec`
        //   );
        // }
      }

      if (isError(data)) {
        toast.error(getErrorMessage(data));
      }
    },
    onError: () => toast.error("WebSocket failed. Refresh to retry."),
    heartbeat: { message: "ping", timeout: 60000 },
  });

  // Stop playing audio
  const stopPlayingAudio = useCallback(() => {
    if (audioRef.current) {
      // Remove any existing event listeners first
      audioRef.current.onended = null;

      // Check if there's actual playback happening
      if (!audioRef.current.paused) {
        audioRef.current.pause();
      }

      // Reset the time regardless of play state
      audioRef.current.currentTime = 0;

      // Update the speaking state
      // setIsSpeaking(false);
    }
  }, [audioRef]);

  const baseFetch = useCallback(
    ({ prompt }: { prompt: string }) => {
      // if (businessProps?.businessId) {

      if (!storedCurrentSessionStartTime) return;

      const getSessionCountdown = (): string => {
        const TEN_MINUTES = 10 * 60 * 1000;
        const sessionEndTime = storedCurrentSessionStartTime + TEN_MINUTES;
        const remainingMs = sessionEndTime - Date.now();

        if (remainingMs <= 0) return "0:00";

        const remainingSeconds = Math.floor(remainingMs / 1000);
        return formatTime(remainingSeconds);
      };

      const dataToSend: MessageData = {
        userMessage: prompt,
        userDataData: isDirectUser
          ? {
              name: userData?.userInfo.name,
              userId: userData?.userInfo.userId,
            }
          : undefined,

        businessUserData: !isDirectUser
          ? {
              name: userData?.userInfo.name,
              userId: userData?.userInfo.userId,
              businessId: businessProps?.businessId,
            }
          : undefined,

        sessionData: {
          countDown: getSessionCountdown(),
          sessionCount: Number(businessProps?.activeSession ?? 1),
        },

        isDirectUser,
        isPcm: !isMediaSourceSupported,
      };

      try {
        sendJsonMessage(dataToSend);
      } catch (error) {
        console.error(error);
      } finally {
        return;
      }
      // }
    },
    [
      businessProps?.activeSession,
      businessProps?.businessId,
      isMediaSourceSupported,
      sendJsonMessage,
      userData?.userInfo.name,
      userData?.userInfo.userId,
      storedCurrentSessionStartTime,
      isDirectUser,
    ]
  );

  // * SENDING MESSAGE
  const sendMessage = useCallback(
    async (tempData?: string) => {
      let temp = tempData;

      if (temp) {
        temp = temp.replace(/Phoebe/gi, "FiBi");
      }

      if (temp && temp.trim()) {
        setFibiProcessing(true);
        const trimmedMessage = temp.trim();
        onInterim("");

        const newMessage: Message = {
          message: trimmedMessage,
          sender: MessageSender.user,
        };
        onAddToChatHistory(newMessage);

        if (isMediaSourceSupported) {
          initNewStream();
          if (sourceBufferRef.current && !sourceBufferRef.current.updating) {
            const buffered = sourceBufferRef.current.buffered;
            if (buffered.length > 0) {
              const removeStart = buffered.start(0); // often this is 0
              const removeEnd = buffered.end(buffered.length - 1);
              sourceBufferRef.current.remove(removeStart, removeEnd);
            }
          }
        } else {
          resetAudioState();
        }

        baseFetch({
          prompt: trimmedMessage,
        });
      }
    },
    [
      baseFetch,
      initNewStream,
      isMediaSourceSupported,
      onAddToChatHistory,
      onInterim,
      sourceBufferRef,
      resetAudioState,
    ]
  );

  useEffect(() => {
    setFibiProcessing(true);
    if (allMessages.length > 0) return;

    baseFetch({
      prompt: "",
    });
  }, [allMessages.length, baseFetch]);

  return {
    isListening: listening,
    businessProps,
    stopListening,
    startListening,
    fibiSpeaking,
    fibiProcessing,
    isNetworkError,
    networkStatus,
  };
};

export default useFiBiVoiceBottomBar;
